import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { X, ArrowRight, AlertCircle, Loader2 } from "lucide-react";

/**
 * No-surprises confirmation dialog shown before a user switches plans.
 * Fetches a preview from the backend (new price, whether an existing sub will be
 * cancelled, estimated prorated refund) and lets the user confirm or cancel.
 */
export default function PlanChangePreviewModal({ open, packageId, onClose, onConfirm }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !packageId) return;
    setPreview(null);
    setError(null);
    setLoading(true);
    api.post("/billing/preview-plan-change", { package_id: packageId })
      .then((r) => setPreview(r.data))
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, packageId]);

  if (!open) return null;

  const fmt = (cents) => `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await onConfirm();
    } catch (e) {
      toast.error(formatApiError(e));
      setConfirming(false);
    }
  };

  const planLabel = (plan) => ({ free: "Free", starter: "Starter", growth: "Growth", pro: "Pro" })[plan] || plan;
  const cycleLabel = (cycle) => cycle === "annual" ? "annual" : "monthly";

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4 overflow-y-auto" data-testid="plan-preview-modal">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl my-8">
        <div className="flex items-center justify-between p-6 border-b border-[#E2E8F0]">
          <h2 className="text-xl font-bold text-[#0F172A]">Confirm plan change</h2>
          <button onClick={onClose} data-testid="plan-preview-close"
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700">
            <X className="h-5 w-5"/>
          </button>
        </div>

        {loading && (
          <div className="p-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-[#F26953]"/> <span className="text-sm text-slate-500">Calculating…</span></div>
        )}

        {error && !loading && (
          <div className="p-6 text-center text-sm text-red-600">{error}</div>
        )}

        {preview && !loading && (
          <div className="p-6 space-y-5" data-testid="plan-preview-content">
            {preview.is_same_plan ? (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5"/>
                <div className="text-sm text-amber-900">You're already on <strong>{planLabel(preview.current_plan)} ({cycleLabel(preview.current_cycle)})</strong>. Nothing to change.</div>
              </div>
            ) : (
              <>
                {/* The switch */}
                <div className="flex items-center justify-between gap-3 rounded-xl border border-[#E2E8F0] bg-slate-50 p-4">
                  <div className="text-center flex-1">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">From</div>
                    <div className="font-semibold text-[#0F172A] mt-1">{preview.is_first_plan ? "—" : planLabel(preview.current_plan)}</div>
                    {!preview.is_first_plan && (
                      <div className="text-[10px] text-slate-400 mt-0.5">{cycleLabel(preview.current_cycle)}</div>
                    )}
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-400 flex-shrink-0"/>
                  <div className="text-center flex-1">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-[#F26953]">To</div>
                    <div className="font-semibold text-[#F26953] mt-1">
                      {preview.is_extra_slot ? "Extra domain" : planLabel(preview.new_plan)}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      {fmt(preview.new_price_cents)} / {preview.new_price_interval}
                    </div>
                  </div>
                </div>

                {/* Breakdown */}
                <ul className="space-y-2 text-sm text-[#475569]">
                  <li className="flex items-start gap-2">
                    <span className="text-[#F26953] flex-shrink-0">•</span>
                    <span>You'll be charged <strong className="text-[#0F172A]">{fmt(preview.new_price_cents)}</strong> today for your new {preview.is_extra_slot ? "extra domain slot" : `${planLabel(preview.new_plan)} plan`}.</span>
                  </li>
                  {preview.will_cancel_prior && (
                    <li className="flex items-start gap-2">
                      <span className="text-[#F26953] flex-shrink-0">•</span>
                      <span>Your current <strong className="text-[#0F172A]">{planLabel(preview.current_plan)}</strong> subscription will be cancelled automatically — no more charges for it.</span>
                    </li>
                  )}
                  {preview.prorated_refund_cents > 0 && (
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-600 flex-shrink-0">•</span>
                      <span>Estimated prorated refund of <strong className="text-emerald-700">{fmt(preview.prorated_refund_cents)}</strong> for unused time on your {planLabel(preview.current_plan)} plan.</span>
                    </li>
                  )}
                  {preview.is_downgrade && (
                    <li className="flex items-start gap-2">
                      <span className="text-amber-600 flex-shrink-0">•</span>
                      <span>This is a <strong>downgrade</strong>. You'll lose access to features above the {planLabel(preview.new_plan)} tier.</span>
                    </li>
                  )}
                  {preview.is_first_plan && (
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-600 flex-shrink-0">•</span>
                      <span>This activates your inbox — you'll be able to connect a domain and create aliases right after checkout.</span>
                    </li>
                  )}
                </ul>
              </>
            )}

            <div className="flex items-center gap-2 pt-2">
              <button onClick={onClose} data-testid="plan-preview-cancel" disabled={confirming}
                className="flex-1 py-2.5 rounded-lg border border-[#E2E8F0] text-[#0F172A] text-sm font-semibold hover:bg-slate-50 disabled:opacity-50">
                Cancel
              </button>
              {!preview.is_same_plan && (
                <button onClick={handleConfirm} data-testid="plan-preview-confirm" disabled={confirming}
                  className="flex-1 py-2.5 rounded-lg bg-[#F26953] text-white text-sm font-semibold hover:bg-[#D85340] disabled:opacity-60 inline-flex items-center justify-center gap-1.5">
                  {confirming ? <><Loader2 className="h-4 w-4 animate-spin"/> Redirecting…</> : <>Continue to checkout <ArrowRight className="h-4 w-4"/></>}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
